'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  'aria-label'?: string;
  'data-testid'?: string;
}

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const getDaysInMonth = (year: number, month: number) =>
  new Date(year, month + 1, 0).getDate();

const getFirstDayOfMonth = (year: number, month: number) =>
  new Date(year, month, 1).getDay();

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  'aria-label': ariaLabel = 'Select date',
  'data-testid': dataTestId = 'date-picker',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  const parsedDate = value
    ? new Date(value.split('T')[0] + 'T00:00:00')
    : new Date();
  const [viewYear, setViewYear] = useState(parsedDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(parsedDate.getMonth());

  useEffect(() => {
    if (value) {
      const d = new Date(value + 'T00:00:00');
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  }, [value]);

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
  }, []);

  const handleOpen = () => {
    updatePosition();
    setIsOpen(true);
  };

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current &&
        !triggerRef.current.contains(target) &&
        calendarRef.current &&
        !calendarRef.current.contains(target)
      ) {
        handleClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, handleClose]);

  const handleSelectDay = (day: number) => {
    const month = String(viewMonth + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    onChange(`${viewYear}-${month}-${dayStr}`);
    handleClose();
  };

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const handleToday = () => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    onChange(`${today.getFullYear()}-${month}-${day}`);
    handleClose();
  };

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const displayValue = value
    ? `${String(parsedDate.getMonth() + 1).padStart(2, '0')}/${String(parsedDate.getDate()).padStart(2, '0')}/${parsedDate.getFullYear()}`
    : '';

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          isOpen ? handleClose() : handleOpen();
        }}
        onMouseDown={(e) => e.stopPropagation()}
        className="w-full flex items-center gap-2 px-3 py-2 bg-white border border-neutral-200 rounded-full
          text-sm hover:border-neutral-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        data-testid={dataTestId}
      >
        <Icon icon={faCalendar} className="w-3.5 h-3.5 text-neutral-500" />
        <span className={displayValue ? 'text-neutral-900' : 'text-neutral-500'}>
          {displayValue || 'Select date'}
        </span>
      </button>

      {isOpen &&
        typeof window !== 'undefined' &&
        createPortal(
          <div
            ref={calendarRef}
            role="dialog"
            aria-label="Date picker calendar"
            className="fixed bg-white rounded-xl shadow-lg border border-neutral-200 p-3 z-[99999] w-[280px]"
            style={{ top: position.top, left: position.left }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            data-testid={`${dataTestId}-calendar`}
          >
            {/* Header */}
            <div className="flex items-center justify-between pb-2">
              <button
                type="button"
                onClick={handlePrevMonth}
                className="p-1.5 rounded-full hover:bg-neutral-100 transition-colors"
                aria-label="Previous month"
                data-testid={`${dataTestId}-prev-month`}
              >
                <Icon icon={faChevronLeft} className="w-3 h-3 text-neutral-600" />
              </button>
              <span className="text-sm font-semibold text-neutral-900">
                {MONTHS[viewMonth]} {viewYear}
              </span>
              <button
                type="button"
                onClick={handleNextMonth}
                className="p-1.5 rounded-full hover:bg-neutral-100 transition-colors"
                aria-label="Next month"
                data-testid={`${dataTestId}-next-month`}
              >
                <Icon icon={faChevronRight} className="w-3 h-3 text-neutral-600" />
              </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 gap-0">
              {DAYS.map((day) => (
                <div
                  key={day}
                  className="text-center text-xs font-medium text-neutral-400 py-1"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Day grid */}
            <div className="grid grid-cols-7 gap-0">
              {Array.from({ length: firstDay }).map((_, i) => (
                <div key={`empty-${i}`} className="h-8" />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const isSelected = dateStr === value;
                const isToday = dateStr === todayStr;

                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleSelectDay(day)}
                    className={`h-8 w-8 mx-auto rounded-full text-sm flex items-center justify-center transition-colors
                      ${isSelected ? 'bg-sky-600 text-white font-semibold' : ''}
                      ${!isSelected && isToday ? 'border border-sky-400 text-sky-600 font-semibold' : ''}
                      ${!isSelected && !isToday ? 'text-neutral-700 hover:bg-neutral-100' : ''}
                    `}
                    aria-label={`${MONTHS[viewMonth]} ${day}, ${viewYear}`}
                    aria-selected={isSelected}
                    data-testid={`${dataTestId}-day-${day}`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="flex justify-end pt-2 border-t border-neutral-100 mt-2">
              <button
                type="button"
                onClick={handleToday}
                className="text-xs font-medium text-primary-600 hover:text-primary-700 px-2 py-1 rounded-md hover:bg-primary-50 transition-colors"
                data-testid={`${dataTestId}-today`}
              >
                Today
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default DatePicker;
